import { useEffect, useRef, useState } from "react";
import { Center, Text, VStack } from "@chakra-ui/react";

type DropzoneOverlayProps = {
  isEnabled: boolean;
  onDropFiles: (files: File[]) => void;
};

const containsFiles = (event: DragEvent) =>
  !!event.dataTransfer?.types.includes("Files");

// Full-window drop target: listens on window so files can be dropped
// anywhere on the page, not only on a dedicated element.
const DropzoneOverlay = ({ isEnabled, onDropFiles }: DropzoneOverlayProps) => {
  const [isDragging, setIsDragging] = useState(false);
  // dragenter/dragleave fire for every child element crossed; only the
  // outermost pair (depth 0) should toggle the overlay.
  const dragDepth = useRef(0);
  // Read via ref so the listener effect doesn't re-subscribe (and reset the
  // drag depth mid-drag) when the parent re-renders with a new callback.
  const onDropFilesRef = useRef(onDropFiles);
  onDropFilesRef.current = onDropFiles;

  useEffect(() => {
    if (!isEnabled) {
      dragDepth.current = 0;
      setIsDragging(false);
      return;
    }

    const handleDragEnter = (event: DragEvent) => {
      if (!containsFiles(event)) return;
      event.preventDefault();
      dragDepth.current += 1;
      setIsDragging(true);
    };

    const handleDragOver = (event: DragEvent) => {
      if (!containsFiles(event)) return;
      // Required, otherwise the browser opens the file instead of firing drop.
      event.preventDefault();
    };

    const handleDragLeave = (event: DragEvent) => {
      if (!containsFiles(event)) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) {
        setIsDragging(false);
      }
    };

    const handleDrop = (event: DragEvent) => {
      if (!containsFiles(event)) return;
      event.preventDefault();
      dragDepth.current = 0;
      setIsDragging(false);
      const files = Array.from(event.dataTransfer?.files ?? []);
      if (files.length > 0) {
        onDropFilesRef.current(files);
      }
    };

    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("drop", handleDrop);
    return () => {
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("drop", handleDrop);
    };
  }, [isEnabled]);

  if (!isDragging) return null;

  return (
    <Center
      position="fixed"
      inset={0}
      // Above page content, sticky headers and any open Select menu (portalled
      // to body at 1500) — dragging a receipt while a filter dropdown is open
      // must still show the drop target.
      //
      // Note this also puts the overlay above modals (1400). It must therefore
      // never be enabled while one is open: the callers own that, and getting it
      // wrong renders "Belege hier ablegen" on top of a dialog that is meant to
      // be blocking, and accepts the drop. The billing gate in transactions.tsx
      // did exactly that.
      zIndex={1600}
      bg="blackAlpha.600"
      // Drop handling happens on window; the overlay itself must not swallow
      // the events.
      pointerEvents="none"
    >
      <Center
        border="2px dashed"
        borderColor="white"
        borderRadius="16px"
        px="16"
        py="12"
      >
        <VStack spacing="2">
          <Text fontSize="2xl" fontWeight="semibold" color="white">
            Belege hier ablegen
          </Text>
          <Text fontSize="md" color="whiteAlpha.800">
            PDF, JPG, PNG, WEBP oder GIF — mehrere Dateien möglich
          </Text>
        </VStack>
      </Center>
    </Center>
  );
};

export default DropzoneOverlay;
